import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Mail, Phone, MapPin, CalendarClock, StickyNote, Activity as ActivityIcon,
  Home, Users2, Inbox, Link2, Ban, Loader2, Sparkles, Clock, Building2,
} from "lucide-react";
import { PageShell } from "@/components/Navbar";
import { Fade, priceLabel } from "@/components/proptech";
import { api, apiError, peso, timeAgo } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { PIPELINE, STATUS_LABEL } from "@/pages/Leads";

const EVENT_LABEL = {
  LEAD_CREATED: "Lead created", INQUIRY_LINKED: "Inquiry linked", STATUS_CHANGED: "Status changed",
  BUYER_REQ_LINKED: "Buyer Need linked", PROPERTY_LINKED: "Property linked",
  FOLLOWUP_CHANGED: "Follow-up updated", NOTE_ADDED: "Note added", COLLAB_LINKED: "Collaboration linked",
  LEAD_CLOSED: "Lead closed", LEAD_LOST: "Lead marked lost", LEAD_UPDATED: "Lead updated", ASSIGNED: "Reassigned",
};

const FUP_TONE = {
  overdue: "bg-critical-soft text-critical", due_today: "bg-attention-soft text-attention",
  upcoming: "bg-info-soft text-info",
};
const FUP_LABEL = { overdue: "Overdue", due_today: "Due today", upcoming: "Upcoming" };
const NBA_TONE = { HIGH: "border-l-critical", MEDIUM: "border-l-gold-500" };

function Empty({ icon: Icon, text }) {
  return (
    <div className="text-center py-10 text-slate-400" data-testid="lead-tab-empty">
      <Icon className="h-8 w-8 mx-auto mb-2 opacity-50" />
      <p className="text-sm">{text}</p>
    </div>
  );
}

export default function LeadDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [lead, setLead] = useState(null);
  const [notes, setNotes] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [followUp, setFollowUp] = useState("");
  const [lostOpen, setLostOpen] = useState(false);
  const [lostReason, setLostReason] = useState("");
  const [linkOpen, setLinkOpen] = useState(false);
  const [myReqs, setMyReqs] = useState([]);
  const [myProps, setMyProps] = useState([]);
  const [linkReq, setLinkReq] = useState("");
  const [linkProp, setLinkProp] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: l }, { data: n }, { data: a }] = await Promise.all([
        api.get(`/leads/${id}`), api.get(`/leads/${id}/notes`), api.get(`/leads/${id}/activity`),
      ]);
      setLead(l); setNotes(n); setActivity(a); setFollowUp(l.follow_up_at ? l.follow_up_at.slice(0, 10) : "");
    } catch (e) { toast.error(apiError(e)); nav("/leads"); } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  const patch = async (payload, msg) => {
    try { await api.patch(`/leads/${id}`, payload); if (msg) toast.success(msg); await load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const addNote = async () => {
    if (!noteText.trim()) return;
    setSavingNote(true);
    try { await api.post(`/leads/${id}/notes`, { text: noteText.trim() }); setNoteText(""); toast.success("Note added"); await load(); }
    catch (e) { toast.error(apiError(e)); } finally { setSavingNote(false); }
  };
  const saveFollowUp = () => patch({ follow_up_at: followUp || "" }, "Follow-up updated");
  const markLost = async () => {
    await patch({ status: "LOST", lost_reason: lostReason }, "Lead marked lost");
    setLostOpen(false); setLostReason("");
  };
  const openLink = async () => {
    setLinkOpen(true);
    try {
      const [{ data: r }, { data: p }] = await Promise.all([
        api.get("/me/buyer-requirements"), api.get("/properties", { params: { scope: "mine" } }),
      ]);
      setMyReqs(Array.isArray(r) ? r : (r.items || []));
      setMyProps(Array.isArray(p) ? p : (p.items || []));
    } catch { /* non-fatal */ }
  };
  const doLinkReq = async () => {
    if (!linkReq) { toast.error("Select a Buyer Need"); return; }
    try { await api.post(`/leads/${id}/link`, { buyer_requirement_id: linkReq }); toast.success("Buyer Need linked"); setLinkOpen(false); setLinkReq(""); await load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const doLinkProp = async () => {
    if (!linkProp) { toast.error("Select a property"); return; }
    try { await api.post(`/leads/${id}/link`, { property_id: linkProp }); toast.success("Property linked"); setLinkOpen(false); setLinkProp(""); await load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (loading || !lead) {
    return <PageShell testid="lead-detail-page"><div className="flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading lead…</div></PageShell>;
  }

  const terminal = lead.status === "CLOSED" || lead.status === "LOST";

  return (
    <PageShell testid="lead-detail-page">
      <button onClick={() => nav("/leads")} data-testid="lead-back" className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4"><ArrowLeft className="h-4 w-4" /> Back to Leads</button>
      <Fade>
        {/* Header */}
        <div className="bg-primary text-primary-foreground rounded-2xl p-5 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="min-w-0">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-accent">Lead · {lead.lead_type}</div>
              <h1 data-testid="lead-name" className="font-display text-2xl sm:text-3xl font-bold mt-0.5 break-words">{lead.contact_name}</h1>
              <div className="flex flex-wrap gap-x-5 gap-y-1.5 mt-3 text-sm text-slate-200">
                {lead.contact_email && <span className="flex items-center gap-1.5 break-all"><Mail className="h-4 w-4 shrink-0" />{lead.contact_email}</span>}
                {lead.contact_mobile && <span className="flex items-center gap-1.5"><Phone className="h-4 w-4 shrink-0" />{lead.contact_mobile}</span>}
                {lead.preferred_location && <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4 shrink-0" />{lead.preferred_location}</span>}
              </div>
              <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-xs text-slate-300">
                <span>Source: {lead.source === "PROFILE_INQUIRY" ? "Profile Inquiry" : lead.source}</span>
                {lead.attribution?.label && <span data-testid="lead-attribution" className="text-accent font-semibold">{lead.attribution.label}</span>}
                <span>Assigned: {lead.assigned_to?.name || lead.assigned_to?.display_name || "You"}</span>
                <span>Last activity {timeAgo(lead.last_activity_at)}</span>
              </div>
            </div>
            <div className="flex flex-col items-stretch sm:items-end gap-2 shrink-0">
              <span className="text-[10px] uppercase tracking-wide text-slate-300">Status</span>
              <Select value={lead.status} onValueChange={(v) => patch({ status: v }, `Moved to ${STATUS_LABEL[v]}`)}>
                <SelectTrigger data-testid="lead-detail-status" className="w-[160px] bg-white text-slate-900"><SelectValue /></SelectTrigger>
                <SelectContent>{[...PIPELINE, "CLOSED", "LOST"].map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {/* Quick actions */}
          <div className="flex flex-wrap items-end gap-2 mt-5 pt-4 border-t border-white/10">
            <div className="flex items-end gap-2">
              <div>
                <label className="block text-[10px] uppercase tracking-wide text-slate-300 mb-1">Next follow-up</label>
                <Input data-testid="lead-followup-input" type="date" value={followUp} onChange={(e) => setFollowUp(e.target.value)} className="h-9 bg-white text-slate-900 w-[160px]" />
              </div>
              <Button data-testid="lead-followup-save" size="sm" onClick={saveFollowUp} className="bg-accent text-accent-foreground hover:bg-accent/90 h-9"><CalendarClock className="h-4 w-4 mr-1" /> Set</Button>
            </div>
            <Button data-testid="lead-link-btn" size="sm" variant="outline" onClick={openLink} className="h-9 bg-white/10 border-white/20 text-white hover:bg-white/20"><Link2 className="h-4 w-4 mr-1" /> Link Buyer Need / Property</Button>
            {!terminal && <Button data-testid="lead-mark-lost" size="sm" variant="outline" onClick={() => setLostOpen(true)} className="h-9 bg-white/10 border-white/20 text-white hover:bg-white/20"><Ban className="h-4 w-4 mr-1" /> Mark Lost</Button>}
          </div>
          {lead.status === "LOST" && lead.lost_reason && <p className="mt-3 text-xs text-slate-300">Lost reason: {lead.lost_reason}</p>}
        </div>

        {/* Tabs */}
        <Tabs defaultValue="overview" className="mt-6">
          <TabsList data-testid="lead-tabs" className="flex flex-wrap h-auto">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="requirements">Requirements</TabsTrigger>
            <TabsTrigger value="matches" data-testid="lead-tab-matches">Matched Properties</TabsTrigger>
            <TabsTrigger value="inquiries">Inquiries</TabsTrigger>
            <TabsTrigger value="notes" data-testid="lead-tab-notes">Notes</TabsTrigger>
            <TabsTrigger value="activity" data-testid="lead-tab-activity">Activity</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-4">
            {lead.next_best_action && (
              <div data-testid="lead-nba" className={`mb-4 flex items-start gap-2.5 rounded-xl border border-ai-border border-l-[3px] ${NBA_TONE[lead.next_best_action.priority] || "border-l-gold-500"} bg-ai-surface px-4 py-3`}>
                <Sparkles className="h-4 w-4 text-gold-500 mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-gold-600">Action Urgency</span>
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${lead.next_best_action.priority === "HIGH" ? "bg-critical-soft text-critical" : "bg-accent/15 text-[#8a6a1f]"}`}>{lead.next_best_action.priority}</span>
                  </div>
                  <p className="text-sm text-slate-700 mt-1 leading-snug">{lead.next_best_action.reason}</p>
                </div>
                <Button data-testid="lead-nba-cta" size="sm" onClick={() => { const el = document.querySelector("[data-testid='lead-followup-input']"); if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.focus(); } }}
                  className="bg-accent text-accent-foreground hover:bg-accent/90 shrink-0 h-8">{lead.next_best_action.action_label}</Button>
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="bg-card border border-border rounded-xl p-4">
                <h3 className="font-semibold text-slate-900 mb-2">Snapshot</h3>
                <dl className="text-sm space-y-1.5 text-slate-600">
                  <div className="flex justify-between"><dt className="text-slate-400">Type</dt><dd>{lead.lead_type}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-400">Lead Priority</dt><dd>{lead.priority}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-400">Budget</dt><dd>{(lead.budget_min || lead.budget_max) ? `${peso(lead.budget_min || 0)}–${peso(lead.budget_max || 0)}` : "—"}</dd></div>
                  <div className="flex justify-between items-center"><dt className="text-slate-400">Next follow-up</dt><dd>{lead.follow_up_at ? (<span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${FUP_TONE[lead.follow_up_bucket] || "bg-secondary text-slate-600"}`}>{lead.follow_up_at.slice(0, 10)} · {FUP_LABEL[lead.follow_up_bucket] || "Set"}</span>) : "None"}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-400">Inquiries</dt><dd>{lead.inquiries?.length || 0}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-400">Created</dt><dd>{timeAgo(lead.created_at)}</dd></div>
                </dl>
              </div>
              <div className="bg-card border border-border rounded-xl p-4">
                <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-1.5"><StickyNote className="h-4 w-4 text-accent" /> Quick note</h3>
                <Textarea data-testid="overview-note-input" value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Log a call, a message, or context…" className="min-h-[90px]" />
                <Button data-testid="overview-note-save" size="sm" onClick={addNote} disabled={savingNote} className="mt-2 bg-accent text-accent-foreground hover:bg-accent/90">{savingNote ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add note"}</Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="requirements" className="mt-4">
            {lead.buyer_requirement ? (
              <div className="bg-card border border-border rounded-xl p-5" data-testid="lead-requirement">
                <h3 className="font-display text-lg font-semibold text-slate-900">{lead.buyer_requirement.buyer_alias}</h3>
                <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-sm text-slate-500">
                  <span><MapPin className="h-4 w-4 inline mr-1" />{(lead.buyer_requirement.cities || []).join(", ") || "—"}</span>
                  <span>{peso(lead.buyer_requirement.budget_min)}–{peso(lead.buyer_requirement.budget_max)}</span>
                  <span>{lead.buyer_requirement.property_type} · {lead.buyer_requirement.listing_type}</span>
                  <span>{lead.buyer_requirement.min_bedrooms}+ BR</span>
                </div>
                <Button size="sm" variant="outline" className="mt-3 rounded-lg" onClick={() => nav(`/buyer/${lead.buyer_requirement.id}/matches`)}>View matches</Button>
              </div>
            ) : <Empty icon={Users2} text="No Buyer Need linked. Use “Link Buyer Need / Property” above." />}
          </TabsContent>

          <TabsContent value="matches" className="mt-4">
            {lead.matched_properties?.length ? (
              <div className="grid sm:grid-cols-2 gap-3" data-testid="lead-matches-list">
                {lead.matched_properties.map((m) => (
                  <div key={m.id} className="bg-card border border-border rounded-xl p-4 flex gap-3">
                    <div className="h-16 w-20 rounded-lg overflow-hidden bg-slate-100 shrink-0">{m.image && <img src={m.image} alt="" className="h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />}</div>
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900 truncate">{m.title}</div>
                      <div className="text-xs text-slate-500">{m.city}</div>
                      <div className="text-sm font-bold text-slate-800 mt-1">{peso(m.price)}</div>
                      <span className="text-xs font-semibold text-accent">{m.score}% match</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : <Empty icon={Home} text="No matched properties. Link a Buyer Need to surface deterministic matches." />}
          </TabsContent>

          <TabsContent value="inquiries" className="mt-4">
            {lead.inquiries?.length ? (
              <div className="space-y-2" data-testid="lead-inquiries-list">
                {lead.inquiries.map((i) => (
                  <div key={i.id} className="bg-card border border-border rounded-xl p-4">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{(i.inquiry_type || "").replace("_", " ")}</span>
                      <span className="text-xs text-slate-400">{timeAgo(i.created_at)}</span>
                    </div>
                    <div className="text-sm text-slate-700 mt-1">{i.subject || i.listing_title || "Listing"}{i.attribution_label ? ` · ${i.attribution_label}` : ""}</div>
                    {i.message && <p className="text-sm text-slate-500 mt-1">{i.message}</p>}
                  </div>
                ))}
              </div>
            ) : <Empty icon={Inbox} text="No inquiries linked to this lead yet." />}
          </TabsContent>

          <TabsContent value="notes" className="mt-4">
            <div className="bg-card border border-border rounded-xl p-4 mb-3">
              <Textarea data-testid="notes-input" value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Add a note…" className="min-h-[80px]" />
              <Button data-testid="notes-save" size="sm" onClick={addNote} disabled={savingNote} className="mt-2 bg-accent text-accent-foreground hover:bg-accent/90">{savingNote ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add note"}</Button>
            </div>
            {notes.length ? (
              <div className="space-y-2" data-testid="notes-list">
                {notes.map((n) => (
                  <div key={n.id} className="bg-card border border-border rounded-xl p-4">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="text-sm font-semibold text-slate-900">{n.author_name}</span>
                      <span className="text-xs text-slate-400">{timeAgo(n.created_at)}</span>
                    </div>
                    <p className="text-sm text-slate-600 whitespace-pre-wrap">{n.text}</p>
                  </div>
                ))}
              </div>
            ) : <Empty icon={StickyNote} text="No notes yet." />}
          </TabsContent>

          <TabsContent value="activity" className="mt-4">
            {activity.length ? (
              <div className="space-y-0" data-testid="activity-timeline">
                {activity.map((a, idx) => (
                  <div key={a.id} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <div className="h-2.5 w-2.5 rounded-full bg-accent mt-1.5" />
                      {idx < activity.length - 1 && <div className="w-px flex-1 bg-border" />}
                    </div>
                    <div className="pb-4 min-w-0">
                      <div className="text-sm font-medium text-slate-900">{EVENT_LABEL[a.event_type] || a.event_type}</div>
                      <div className="text-xs text-slate-400">{a.actor_name} · {timeAgo(a.created_at)}</div>
                      {a.event_type === "STATUS_CHANGED" && a.meta?.to && <div className="text-xs text-slate-500 mt-0.5">{STATUS_LABEL[a.meta.from] || a.meta.from} → {STATUS_LABEL[a.meta.to] || a.meta.to}</div>}
                    </div>
                  </div>
                ))}
              </div>
            ) : <Empty icon={ActivityIcon} text="No activity recorded yet." />}
          </TabsContent>
        </Tabs>
      </Fade>

      {/* Mark Lost dialog */}
      <Dialog open={lostOpen} onOpenChange={setLostOpen}>
        <DialogContent data-testid="lead-lost-dialog">
          <DialogHeader>
            <DialogTitle>Mark lead as Lost?</DialogTitle>
            <DialogDescription>This removes the lead from the active pipeline. You can add an optional reason.</DialogDescription>
          </DialogHeader>
          <Textarea data-testid="lost-reason-input" value={lostReason} onChange={(e) => setLostReason(e.target.value)} placeholder="Optional reason (e.g. bought elsewhere, budget mismatch)…" className="min-h-[80px]" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setLostOpen(false)}>Cancel</Button>
            <Button data-testid="lost-confirm" onClick={markLost} className="bg-critical hover:bg-critical/90 text-white">Mark Lost</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link dialog */}
      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent data-testid="lead-link-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Link2 className="h-5 w-5 text-accent" /> Link records</DialogTitle>
            <DialogDescription>Reference an existing Buyer Need or property (no data is duplicated). Each is linked separately.</DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            <div className="rounded-xl border border-border p-3">
              <div className="flex items-center gap-2 mb-2"><Users2 className="h-4 w-4 text-primary" /><span className="text-sm font-semibold text-slate-900">Link a Buyer Need</span></div>
              <div className="flex gap-2">
                <Select value={linkReq} onValueChange={setLinkReq}>
                  <SelectTrigger data-testid="link-req-select" className="flex-1"><SelectValue placeholder="Select a Buyer Need" /></SelectTrigger>
                  <SelectContent>{myReqs.map((r) => <SelectItem key={r.id} value={r.id}>{r.buyer_alias || r.id}</SelectItem>)}</SelectContent>
                </Select>
                <Button data-testid="link-req-confirm" onClick={doLinkReq} className="bg-primary text-white hover:bg-primary/90 shrink-0">Link Buyer Need</Button>
              </div>
              {myReqs.length === 0 && <p className="text-xs text-slate-400 mt-1.5">You have no Buyer Needs to link.</p>}
            </div>
            <div className="rounded-xl border border-border p-3">
              <div className="flex items-center gap-2 mb-2"><Building2 className="h-4 w-4 text-accent" /><span className="text-sm font-semibold text-slate-900">Link a Property</span></div>
              <div className="flex gap-2">
                <Select value={linkProp} onValueChange={setLinkProp}>
                  <SelectTrigger data-testid="link-prop-select" className="flex-1"><SelectValue placeholder="Select a property" /></SelectTrigger>
                  <SelectContent>{myProps.map((p) => <SelectItem key={p.id} value={p.id}>{p.title || p.id}</SelectItem>)}</SelectContent>
                </Select>
                <Button data-testid="link-prop-confirm" onClick={doLinkProp} className="bg-accent text-accent-foreground hover:bg-accent/90 shrink-0">Link Property</Button>
              </div>
              {myProps.length === 0 && <p className="text-xs text-slate-400 mt-1.5">You have no properties to link.</p>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
